/**
 * Shared frontend types for the manga creation flow (components/manga/*).
 * Server-side contracts live in types/manga.ts.
 */
import { normalizeHeadCovering } from '@/utils/manga/head-covering';
import { MAX_COMIC_CHARACTERS } from '@/types/manga';
import type { PhotoScene } from '@/utils/picture-book/photo-scene';

export interface FaceBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Shape returned by POST /api/detect-faces (see app/api/detect-faces/route.ts) */
export interface DetectedFace {
  id: string;
  number: number;
  bbox: FaceBox;
  expanded_bbox?: FaceBox;
  confidence: number;
  thumbnail?: string;
}

/** Uploaded photo + detection results, held by the create-comic page */
export interface MangaPhoto {
  /** data URL (sent to the API as photoBase64) */
  dataUrl: string;
  dimensions: { width: number; height: number };
  faces: DetectedFace[];
  /** Per-face age/gender estimate, keyed person_<face index> (same API as the picture-book wizard) */
  analysis?: PhotoAnalysis;
  /** Setting/season cue from the same analysis call (picture-book flow only). */
  scene?: PhotoScene | null;
  /** The consent version the parent ticked before this photo was used (sent as photoConsent, R3). */
  photoConsentVersion?: string;
  /** The photo came from the free cover (?fromCover=): no tick asked, the server inherits the cover's consent (R5). */
  fromSeed?: boolean;
}

/** One entry from POST /api/analyze-multi-character-photo */
export interface FaceAnalysis {
  estimatedAge?: number;
  gender?: string;
  confidence?: number;
  /** A religious or cultural head covering seen in the photo (the server normalises it). */
  headCovering?: string;
}

export type PhotoAnalysis = Record<string, FaceAnalysis | undefined>;

/**
 * Turn one face's analysis into hero-card values. The service folds adults into
 * boy/girl for the picture-book UI, so age decides man/woman here.
 * Returns null when the analysis failed (confidence 0 means random fallback values).
 */
export function heroFromAnalysis(
  a: FaceAnalysis | undefined
): Partial<Pick<MangaCharacterDraft, 'age' | 'gender' | 'headCovering'>> | null {
  if (!a || !a.confidence) return null;
  const out: Partial<Pick<MangaCharacterDraft, 'age' | 'gender' | 'headCovering'>> = {};
  const rawAge = Number(a.estimatedAge);
  const age = Number.isFinite(rawAge) ? Math.min(Math.max(Math.round(rawAge), 1), 99) : null;
  if (age !== null) out.age = String(age);
  const g = String(a.gender || '').toLowerCase();
  const female = g === 'girl' || g === 'woman' || g === 'female';
  const male = g === 'boy' || g === 'man' || g === 'male';
  if (female || male) {
    const adult = age !== null ? age >= 18 : g === 'man' || g === 'woman';
    out.gender = female ? (adult ? 'woman' : 'girl') : adult ? 'man' : 'boy';
  }
  // Kept on every page and the cover whatever the story's costumes (utils/manga/head-covering.ts)
  const headCovering = normalizeHeadCovering(a.headCovering);
  if (headCovering) out.headCovering = headCovering;
  return out;
}

/** One hero card in step 2 */
export interface MangaCharacterDraft {
  id: string;
  name: string;
  /** kept as string for the input; converted to number when submitting */
  age: string;
  gender: string;
  relationship: string;
  isMainCharacter: boolean;
  faceCoordinates: FaceBox | null;
  /** small face crop from detection, for the card header */
  thumbnail?: string;
  /** A religious or cultural head covering from the photo analysis (sent with the person; shown on the person card with a remove button). */
  headCovering?: string;
  /** True once the gender came from the photo analysis or the parent's pick (not the 'boy' default); words the chip. */
  genderKnown?: boolean;
}

/** Same cap as the preview route (types/manga.ts is the single source of truth). */
export const MAX_MANGA_CHARACTERS = MAX_COMIC_CHARACTERS;

export function createEmptyCharacter(isMain: boolean): MangaCharacterDraft {
  return {
    id: `char_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: '',
    age: '8',
    gender: 'boy',
    relationship: '',
    isMainCharacter: isMain,
    faceCoordinates: null,
  };
}

/** Pre-create one character per detected face (in detection order), capped, pre-filled from analysis */
export function charactersFromFaces(
  faces: DetectedFace[],
  analysis?: PhotoAnalysis
): MangaCharacterDraft[] {
  if (!faces || faces.length === 0) {
    return [createEmptyCharacter(true)];
  }
  return faces.slice(0, MAX_MANGA_CHARACTERS).map((face, index) => {
    const hero = heroFromAnalysis(analysis?.[`person_${index}`]);
    return {
      ...createEmptyCharacter(index === 0),
      id: `char_face_${face.id ?? index}_${Math.random().toString(36).slice(2, 6)}`,
      faceCoordinates: face.bbox || null,
      thumbnail: face.thumbnail,
      ...hero,
      // Only an analysed gender counts as known; the 'boy' default does not (head-covering chip wording)
      ...(hero?.gender ? { genderKnown: true } : {}),
    };
  });
}
